// =============================================================================
// Accountant Investor Detail Page
// =============================================================================
// Detailed investor profile view for K-1 preparation
// =============================================================================

import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  EyeIcon,
  DocumentTextIcon,
  CloudArrowUpIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  XMarkIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, DocumentViewerModal } from '../../components/ui';
import { AuthenticatorModal } from '../../components/ui/AuthenticatorModal';
import {
  accountantService,
  hasTotpSession,
  setTotpSession,
  type AccountantProfileDetail,
  type AccountantDistributionSummary,
} from '../../services/accountant.service';
import { formatDate } from '../../utils/dateFormat';

// =============================================================================
// Upload K1 Modal
// =============================================================================

interface UploadK1ModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileId: string;
  profileName: string;
  deals: { id: string; name: string }[];
  currentDealId?: string;
  availableTaxYears?: number[];
  onUploaded: () => void;
}

function UploadK1Modal({ isOpen, onClose, profileId, profileName, deals, currentDealId, availableTaxYears, onUploaded }: UploadK1ModalProps) {
  // Use provided availableTaxYears or default to current year - 1
  const defaultYear = availableTaxYears && availableTaxYears.length > 0
    ? availableTaxYears[0]
    : new Date().getFullYear() - 1;
  const [file, setFile] = useState<File | null>(null);
  const [taxYear, setTaxYear] = useState(defaultYear);
  const [dealId, setDealId] = useState<string>(currentDealId || (deals.length === 1 ? deals[0].id : ''));
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !dealId) {
      setError('Please select a deal and upload a file.');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      await accountantService.uploadK1Document(profileId, file, taxYear, dealId);
      onUploaded();
      onClose();
    } catch (err) {
      console.error('Failed to upload K-1:', err);
      setError('Failed to upload K-1 document. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Upload K-1 Document</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Uploading K-1 for: <span className="font-medium text-gray-900">{profileName}</span>
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tax Year *</label>
            <select
              value={taxYear}
              onChange={(e) => setTaxYear(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              {(availableTaxYears && availableTaxYears.length > 0
                ? availableTaxYears
                : [new Date().getFullYear() - 1]
              ).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal *</label>
            {deals.length === 0 ? (
              <p className="text-sm text-red-500">No deals available for this investor.</p>
            ) : (
              <select
                value={dealId}
                onChange={(e) => setDealId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                required
              >
                {deals.length > 1 && <option value="">Select a deal</option>}
                {deals.map((deal) => (
                  <option key={deal.id} value={deal.id}>
                    {deal.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">K-1 Document *</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-500 transition-colors">
              <input
                type="file"
                accept=".pdf,.PDF,.xls,.xlsx,.doc,.docx"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                id="k1-file-input"
              />
              <label htmlFor="k1-file-input" className="cursor-pointer">
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <DocumentTextIcon className="h-6 w-6 text-primary-600" />
                    <span className="text-sm font-medium text-gray-900">{file.name}</span>
                  </div>
                ) : (
                  <div>
                    <CloudArrowUpIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Click to select a file</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, Excel, or Word documents</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isUploading || !file || !dealId}>
              {isUploading ? 'Uploading...' : 'Upload K-1'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function AccountantInvestorDetailPage() {
  const { dealId, profileId } = useParams<{ dealId?: string; profileId: string }>();
  const [searchParams] = useSearchParams();
  const taxYearParam = searchParams.get('taxYear');
  const [isLoading, setIsLoading] = useState(true);
  const [profile, setProfile] = useState<AccountantProfileDetail | null>(null);
  const [distributions, setDistributions] = useState<AccountantDistributionSummary | null>(null);
  const [selectedYear, setSelectedYear] = useState(
    taxYearParam ? parseInt(taxYearParam) : new Date().getFullYear() - 1
  );
  const [error, setError] = useState<string | null>(null);

  // Reveal state for SSN/FEIN (click-and-hold)
  const [revealedField, setRevealedField] = useState<'ssn' | 'fein' | null>(null);
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const [revealingField, setRevealingField] = useState<'ssn' | 'fein' | null>(null);
  const isHoldingRef = useRef(false);

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Available tax years for the current deal
  const [availableTaxYears, setAvailableTaxYears] = useState<number[]>([]);

  // Document viewer state
  const [viewingDocument, setViewingDocument] = useState<{ id: string; name: string } | null>(null);

  // Delete confirmation state
  const [deletingDocument, setDeletingDocument] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // TOTP verification state
  const [showTotpModal, setShowTotpModal] = useState(false);
  const [pendingRevealType, setPendingRevealType] = useState<'ssn' | 'fein' | null>(null);

  useEffect(() => {
    if (profileId) {
      loadProfile();
    }
  }, [profileId]);

  useEffect(() => {
    if (profileId) {
      loadDistributions();
    }
  }, [profileId, selectedYear]);

  // Load available tax years when we have a dealId
  useEffect(() => {
    if (dealId) {
      accountantService.getDealInvestors(dealId).then((data) => {
        setAvailableTaxYears(data.availableTaxYears || []);
      }).catch(console.error);
    }
  }, [dealId]);

  const loadProfile = async () => {
    if (!profileId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await accountantService.getInvestorProfile(profileId);
      setProfile(data);
    } catch (err) {
      console.error('Failed to load profile:', err);
      setError('Failed to load investor profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDistributions = async () => {
    if (!profileId) return;
    try {
      const data = await accountantService.getProfileDistributions(profileId, selectedYear);
      setDistributions(data);
    } catch (err) {
      console.error('Failed to load distributions:', err);
    }
  };

  // Start revealing (called on mouse down / touch start)
  const handleRevealStart = async (dataType: 'ssn' | 'fein') => {
    if (!profileId || revealingField) return;

    // Check TOTP session before proceeding
    if (!hasTotpSession()) {
      setPendingRevealType(dataType);
      setShowTotpModal(true);
      return;
    }

    isHoldingRef.current = true;
    setRevealingField(dataType);

    try {
      const result = await accountantService.revealSensitiveData(profileId, dataType);
      // Only show if still holding (user hasn't released yet)
      if (isHoldingRef.current) {
        setRevealedField(dataType);
        setRevealedValue(result.value);
      }
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'TOTP_REQUIRED' || code === 'TOTP_NOT_SETUP') {
        setPendingRevealType(dataType);
        setShowTotpModal(true);
      } else {
        console.error('Failed to reveal data:', err);
      }
    } finally {
      setRevealingField(null);
    }
  };

  const handleTotpVerified = () => {
    setTotpSession();
    setShowTotpModal(false);
    // Don't auto-trigger reveal - user needs to hold-to-reveal again after verification
    setPendingRevealType(null);
  };

  // Stop revealing (called on mouse up / mouse leave / touch end)
  const handleRevealEnd = () => {
    isHoldingRef.current = false;
    setRevealedField(null);
    setRevealedValue(null);
  };

  // Delete K-1 document
  const handleDeleteDocument = async () => {
    if (!deletingDocument || deleteConfirmText.toLowerCase() !== 'proceed') return;
    setIsDeleting(true);
    try {
      await accountantService.deleteDocument(deletingDocument.id);
      setDeletingDocument(null);
      setDeleteConfirmText('');
      loadProfile(); // Refresh to update the list
    } catch (err) {
      console.error('Failed to delete document:', err);
      setError('Failed to delete document. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatSSN = (ssn: string): string => {
    const digits = ssn.replace(/\D/g, '');
    if (digits.length === 9) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    }
    return ssn;
  };

  const formatFEIN = (fein: string): string => {
    const digits = fein.replace(/\D/g, '');
    if (digits.length === 9) {
      return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    }
    return fein;
  };

  const getProfileTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      INDIVIDUAL: 'Individual',
      LLC: 'LLC',
      TRUST: 'Trust',
      CORPORATION: 'Corporation',
      PARTNERSHIP: 'Partnership',
      IRA: 'IRA',
    };
    return labels[type] || type;
  };

  // Use deal's available tax years for dropdown, or default to current year - 1
  const distributionYears = availableTaxYears.length > 0
    ? availableTaxYears
    : [new Date().getFullYear() - 1];

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-32 bg-gray-200 rounded-xl" />
        <div className="h-64 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  // Determine the back link based on whether we came from a deal page
  const backLink = dealId ? `/accountant/deals/${dealId}` : '/accountant/investors';
  const backText = dealId ? 'Back to Deal' : 'Back to Investors';

  if (error || !profile) {
    return (
      <div className="space-y-6">
        <Link
          to={backLink}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {backText}
        </Link>
        <Card padding="lg">
          <div className="text-center py-12">
            <UserCircleIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              {error || 'Profile not found'}
            </h3>
            <Button className="mt-4" onClick={loadProfile}>
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={backLink}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </Link>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 rounded-lg">
              {profile.type === 'INDIVIDUAL' ? (
                <UserCircleIcon className="h-8 w-8 text-primary-600" />
              ) : (
                <BuildingOfficeIcon className="h-8 w-8 text-primary-600" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {profile.entityName || profile.legalName}
                </h1>
                <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                  {getProfileTypeLabel(profile.type)}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                {profile.investor.name} - {profile.investor.email}
              </p>
            </div>
          </div>
        </div>
        <Button onClick={() => setShowUploadModal(true)} className="flex items-center gap-2">
          <CloudArrowUpIcon className="h-5 w-5" />
          Upload K-1
        </Button>
      </div>

      {/* Profile Info Card */}
      <Card padding="md">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {profile.authorizedSigner && (
            <div>
              <p className="text-xs text-gray-500">Authorized Signer</p>
              <p className="text-sm font-medium text-gray-900">{profile.authorizedSigner}</p>
            </div>
          )}
          {profile.stateOfFormation && (
            <div>
              <p className="text-xs text-gray-500">State of Formation</p>
              <p className="text-sm font-medium text-gray-900">{profile.stateOfFormation}</p>
            </div>
          )}
          {profile.address && (
            <div>
              <p className="text-xs text-gray-500">Address</p>
              <p className="text-sm font-medium text-gray-900">
                {profile.address}, {profile.city}, {profile.state} {profile.zipCode}
              </p>
            </div>
          )}

          {/* Tax ID Section */}
          {(profile.hasSSN || profile.hasFEIN) && (
            <div className="col-span-full border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Tax Identification</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profile.hasSSN && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-500">Social Security Number</p>
                      <p className="text-sm font-medium text-gray-900">
                        {revealedField === 'ssn'
                          ? formatSSN(revealedValue || '')
                          : `***-**-${profile.ssnLastFour}`}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={revealingField === 'ssn'}
                      onMouseDown={() => handleRevealStart('ssn')}
                      onMouseUp={handleRevealEnd}
                      onMouseLeave={handleRevealEnd}
                      onTouchStart={() => handleRevealStart('ssn')}
                      onTouchEnd={handleRevealEnd}
                      className="select-none"
                    >
                      {revealingField === 'ssn' ? (
                        <span className="animate-spin h-4 w-4 mr-1 border-2 border-gray-300 border-t-primary-600 rounded-full" />
                      ) : (
                        <EyeIcon className="h-4 w-4 mr-1" />
                      )}
                      {revealedField === 'ssn' ? 'Release to hide' : revealingField === 'ssn' ? 'Loading...' : 'Hold to reveal'}
                    </Button>
                  </div>
                )}
                {profile.hasFEIN && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-500">Federal EIN</p>
                      <p className="text-sm font-medium text-gray-900">
                        {revealedField === 'fein'
                          ? formatFEIN(revealedValue || '')
                          : `**-***${profile.feinLastFour}`}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={revealingField === 'fein'}
                      onMouseDown={() => handleRevealStart('fein')}
                      onMouseUp={handleRevealEnd}
                      onMouseLeave={handleRevealEnd}
                      onTouchStart={() => handleRevealStart('fein')}
                      onTouchEnd={handleRevealEnd}
                      className="select-none"
                    >
                      {revealingField === 'fein' ? (
                        <span className="animate-spin h-4 w-4 mr-1 border-2 border-gray-300 border-t-primary-600 rounded-full" />
                      ) : (
                        <EyeIcon className="h-4 w-4 mr-1" />
                      )}
                      {revealedField === 'fein' ? 'Release to hide' : revealingField === 'fein' ? 'Loading...' : 'Hold to reveal'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Investments Card */}
      <Card padding="md">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          <BanknotesIcon className="h-5 w-5 inline mr-2 text-gray-400" />
          Investments
        </h2>
        {profile.investments.length === 0 ? (
          <p className="text-sm text-gray-500">No investments found for this profile.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Deal
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Investment Amount
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Ownership %
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {profile.investments.map((inv) => (
                  <tr key={inv.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{inv.dealName}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900">
                      {formatCurrency(inv.investmentAmount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-500">
                      {inv.ownershipPercentage.toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">Total</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">
                    {formatCurrency(profile.investments.reduce((sum, inv) => sum + inv.investmentAmount, 0))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Distributions Card */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            <CalendarDaysIcon className="h-5 w-5 inline mr-2 text-gray-400" />
            Distributions
          </h2>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            {distributionYears.map((year) => (
              <option key={year} value={year}>
                Tax Year {year}
              </option>
            ))}
          </select>
        </div>

        {!distributions || distributions.distributionCount === 0 ? (
          <p className="text-sm text-gray-500">
            No distributions found for tax year {selectedYear}.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-700">
                Total Distributed in {selectedYear}:{' '}
                <span className="font-bold text-green-900">
                  {formatCurrency(distributions.totalDistributed)}
                </span>
                <span className="text-green-600 ml-2">
                  ({distributions.distributionCount} distribution{distributions.distributionCount !== 1 ? 's' : ''})
                </span>
              </p>
            </div>

            {distributions.byDeal.map((deal) => (
              <div key={deal.dealId} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-gray-900">{deal.dealName}</p>
                    <p className="text-sm font-semibold text-green-600">
                      {formatCurrency(deal.totalDistributed)}
                    </p>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {deal.distributions.map((dist) => (
                    <div key={dist.id} className="px-4 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-500">{formatDate(dist.date)}</span>
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                          {dist.type}
                        </span>
                        {dist.notes && (
                          <span className="text-xs text-gray-400">{dist.notes}</span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(dist.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* K-1 Documents Card */}
      <Card padding="md">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          <DocumentTextIcon className="h-5 w-5 inline mr-2 text-gray-400" />
          K-1 Documents
        </h2>
        {profile.k1Documents.length === 0 ? (
          <div className="text-center py-8">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <p className="mt-2 text-sm text-gray-500">No K-1 documents uploaded yet.</p>
            <Button className="mt-4" onClick={() => setShowUploadModal(true)}>
              <CloudArrowUpIcon className="h-4 w-4 mr-2" />
              Upload K-1
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {profile.k1Documents.map((doc) => (
              <div
                key={doc.id}
                className="py-3 flex items-center justify-between hover:bg-gray-50 px-2 rounded"
              >
                <div
                  className="flex items-center gap-3 flex-1 cursor-pointer"
                  onClick={() => setViewingDocument({ id: doc.id, name: doc.name })}
                >
                  <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                    <p className="text-xs text-gray-500">
                      Tax Year {doc.taxYear} - Uploaded {formatDate(doc.createdAt)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setViewingDocument({ id: doc.id, name: doc.name })}
                  >
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 border-red-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirmText('');
                      setDeletingDocument({ id: doc.id, name: doc.name });
                    }}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Upload K-1 Modal */}
      <UploadK1Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        profileId={profileId!}
        profileName={profile.entityName || profile.legalName || ''}
        deals={profile.investments.map((inv) => ({ id: inv.dealId, name: inv.dealName }))}
        currentDealId={dealId}
        availableTaxYears={availableTaxYears}
        onUploaded={loadProfile}
      />

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewerModal
          isOpen={!!viewingDocument}
          onClose={() => setViewingDocument(null)}
          documentId={viewingDocument.id}
          fileName={viewingDocument.name}
          getEmbedUrls={accountantService.getDocumentViewUrl}
        />
      )}

      {/* TOTP Verification Modal */}
      <AuthenticatorModal
        isOpen={showTotpModal}
        onClose={() => {
          setShowTotpModal(false);
          setPendingRevealType(null);
        }}
        onVerified={handleTotpVerified}
        title="Verification Required"
        description={`Viewing sensitive ${pendingRevealType === 'fein' ? 'EIN' : 'SSN'} information requires two-factor verification. Please verify with your authenticator app to proceed.`}
      />

      {/* Delete Confirmation Modal */}
      {deletingDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete K-1 Document</h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              You are about to permanently delete this document:
            </p>
            <p className="text-sm font-medium text-gray-900 mb-4 p-2 bg-gray-50 rounded-lg">
              {deletingDocument.name}
            </p>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> below to confirm deletion:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeletingDocument(null);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteDocument}
                disabled={deleteConfirmText.toLowerCase() !== 'proceed' || isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Deleting...
                  </>
                ) : (
                  'Delete Document'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountantInvestorDetailPage;
